import type { CategoryGroup, Category } from '@/types/category'

// Seed data — the canonical category list, also used as fallback when Firestore is empty
export const SEED_CATEGORY_GROUPS: CategoryGroup[] = [
  { id: 'marketplace', name: 'Marketplace', icon: 'marketplace' },
  { id: 'automotive', name: 'Automotive', icon: 'automotive' },
  { id: 'fundraiser', name: 'Fundraiser', icon: 'fundraiser' },
]

export const SEED_CATEGORIES: Category[] = [
  // Marketplace
  { id: 'dollar-reserve', name: '$1 Reserve', group: 'marketplace', order: 1 },
  { id: 'art-antiques', name: 'Art & Antiques', group: 'marketplace', order: 2 },
  { id: 'auto-parts', name: 'Auto Parts', group: 'marketplace', order: 3 },
  { id: 'bicycles', name: 'Bicycles', group: 'marketplace', order: 4 },
  { id: 'books-magazines', name: 'Books & Magazines', group: 'marketplace', order: 5 },
  { id: 'building-renovation', name: 'Building & Renovation', group: 'marketplace', order: 6 },
  { id: 'electronics', name: 'Electronics', group: 'marketplace', order: 7 },
  { id: 'health-beauty', name: 'Health & Beauty', group: 'marketplace', order: 8 },
  { id: 'home-living', name: 'Home & Living', group: 'marketplace', order: 9 },
  { id: 'hunting-fishing', name: 'Hunting & Fishing', group: 'marketplace', order: 10 },
  { id: 'music-instruments', name: 'Music & Instruments', group: 'marketplace', order: 11 },
  { id: 'jewellery-watches', name: 'Jewellery & Watches', group: 'marketplace', order: 12 },
  { id: 'clothing', name: 'Clothing', group: 'marketplace', order: 13 },
  { id: 'party-events', name: 'Party & Events', group: 'marketplace', order: 14 },
  { id: 'pets-animals', name: 'Pets & Animals', group: 'marketplace', order: 15 },
  { id: 'sports-outdoors', name: 'Sports & Outdoors', group: 'marketplace', order: 16 },

  // Automotive (includes merged Farming & Machinery sub-categories)
  { id: 'vehicle', name: 'Cars & Vehicles', group: 'automotive', order: 1, listingType: 'vehicle' },
  { id: 'motorcycle', name: 'Motorcycles', group: 'automotive', order: 2, listingType: 'vehicle' },
  { id: 'off-road-bike', name: 'Off-Road Bikes', group: 'automotive', order: 3, listingType: 'vehicle' },
  { id: 'marine', name: 'Marine', group: 'automotive', order: 4, listingType: 'vehicle' },
  { id: 'quad-atv', name: 'Quad & ATV', group: 'automotive', order: 5, listingType: 'vehicle' },
  { id: 'everything-machinery', name: 'Machinery', group: 'automotive', order: 6 },
  { id: 'farming', name: 'Farming', group: 'automotive', order: 7 },
  { id: 'farm-bikes-vehicles', name: 'Farm Bikes & Vehicles', group: 'automotive', order: 8, listingType: 'vehicle' },

  // Fundraiser
  { id: 'fundraiser-auction', name: 'Auction', group: 'fundraiser', order: 1, listingType: 'fundraiser' },
  { id: 'fundraiser-products', name: 'Products', group: 'fundraiser', order: 2, listingType: 'fundraiser' },
  { id: 'fundraiser-raffle', name: 'Raffle', group: 'fundraiser', order: 3, listingType: 'fundraiser' },
  { id: 'fundraiser-sponsorship', name: 'Sponsorship', group: 'fundraiser', order: 4, listingType: 'fundraiser' },
]

// Module-level cache — populated by useCategories hook from Firestore, falls back to seed data
let _categoryGroups: CategoryGroup[] = SEED_CATEGORY_GROUPS
let _categories: Category[] = SEED_CATEGORIES

/** Called by useCategories hook to update the module cache with Firestore data */
export function setCategoryCache(groups: CategoryGroup[], categories: Category[]) {
  _categoryGroups = groups
  _categories = categories
}

// Public exports — used throughout the app, read from cache (Firestore when available, seed data as fallback)
export function getCategoryGroups(): CategoryGroup[] {
  return _categoryGroups
}

export function getAllCategories(): Category[] {
  return _categories
}

// Keep these named exports for backward compat with existing imports
export const CATEGORY_GROUPS = SEED_CATEGORY_GROUPS
export const CATEGORIES = SEED_CATEGORIES

// Helper functions
export function getCategoryById(id: string): Category | undefined {
  return _categories.find((cat) => cat.id === id)
}

export function getCategoriesByGroup(groupId: string): Category[] {
  return _categories.filter((cat) => cat.group === groupId).sort((a, b) => a.order - b.order)
}

export function getCategoryGroupById(id: string): CategoryGroup | undefined {
  return _categoryGroups.find((group) => group.id === id)
}
